import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Home, Search, ShoppingBag, Heart, Menu, LogOut, User, TrendingUp, Calendar, Newspaper, Trophy, Users, Ticket, Store, Gavel, Award, Briefcase, X, Building2, GanttChart, Sparkles, ShieldAlert, HeartHandshake, PackageOpen, ShieldCheck, Lock, Megaphone, Bot, Crown, Zap, Mail, MailOpen } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { files as filesApi, messagesApi } from '../services/api';
import { useMyTier } from '../hooks/useMyTier';
import NotificationsBell from './NotificationsBell';
import WelcomeModal from './WelcomeModal';

const NAV = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/marketing', label: 'Public Site', icon: Store },
  { to: '/plans', label: 'Choose Plan', icon: Crown, highlight: true, badge: 'Buy', badgeTint: 'yellow' },
  { to: '/directory', label: 'Directory', icon: Search },
  { to: '/auction', label: 'Auction Store', icon: Gavel },
  { to: '/dealbin', label: 'Deal Bin', icon: Sparkles },
  { to: '/donate', label: 'Donate an Item', icon: HeartHandshake },
  { to: '/store', label: 'Online Store', icon: ShoppingBag },
  { to: '/loyalty', label: 'Loyalty Points', icon: Award },
  { to: '/coupons', label: 'Coupons', icon: Ticket },
  { to: '/community', label: 'Community', icon: Users },
  { to: '/messages', label: 'Messages', icon: Mail, unread: true },
  { to: '/newsletter', label: 'Newsletter', icon: MailOpen },
  { to: '/blackout', label: 'Blackout Game', icon: Zap, highlight: true, badge: 'New', badgeTint: 'emerald' },
  { to: '/groups', label: 'Groups', icon: HeartHandshake },
  { to: '/events', label: 'Events', icon: Calendar },
  { to: '/my/tickets', label: 'My Tickets', icon: Ticket },
  { to: '/dating', label: 'Dating', icon: Heart },
  { to: '/news', label: 'News Feed', icon: Newspaper },
  { to: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  { to: '/funds', label: 'Community Funds', icon: TrendingUp },
];

const BUSINESS_NAV = [
  { to: '/business-dashboard', label: 'Business Dashboard', icon: Briefcase, feature: 'business_info' },
  { to: '/business/manage', label: 'Manage Listings', icon: Building2, feature: 'manage_listings' },
  { to: '/business/coupons', label: 'My Coupons', icon: Ticket, feature: 'coupons' },
  { to: '/business/loyalty', label: 'Loyalty Program', icon: Award, feature: 'loyalty' },
  { to: '/business/reviews', label: 'Review Verification', icon: ShieldCheck, feature: 'reviews_verification' },
  { to: '/business/auctions', label: 'My Auction Items', icon: GanttChart, feature: 'auction_items' },
  { to: '/marketing/landing', label: 'Landing Page Builder', icon: Sparkles, feature: 'landing_page_builder' },
  { to: '/marketing/surveys', label: 'Surveys', icon: Sparkles, feature: 'landing_page_builder' },
  { to: '/marketing/campaigns', label: 'Campaigns', icon: Megaphone, feature: 'landing_page_builder' },
  { to: '/marketing/auto-responder', label: 'Auto-Responder', icon: Bot, feature: 'landing_page_builder' },
  { to: '/marketing/contacts', label: 'Contact List', icon: Users, feature: 'landing_page_builder' },
  { to: '/business/upgrade', label: 'Upgrade Plan', icon: Sparkles, feature: null },
];

const BOTTOM = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/directory', label: 'Directory', icon: Search },
  { to: '/auction', label: 'Auction', icon: Gavel },
  { to: '/donate', label: 'Donate', icon: PackageOpen },
  { to: '/profile', label: 'Profile', icon: User },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();
  // Only real business owners fetch /my-tier; plain users get null (all locks off).
  const canShowTier = !!user?.isBusinessOwner || !!user?.isAdmin;
  const { tier } = useMyTier();  // hook always called; internally checks auth
  const hasFeature = (f) => !f || !canShowTier || !tier?.features || tier.features.includes(f);

  const doLogout = () => { logout(); navigate('/login'); };
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const tick = () => messagesApi.unread().then(d => setUnread(d.count)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [loc.pathname, loc.search]);

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-[#0a0a0a] border-r border-[#1f1f1f] z-40 transform ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex items-center gap-3 px-5 py-5 border-b border-[#1f1f1f]">
          <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center font-black text-black">PBB</div>
          <div>
            <div className="font-semibold">Promote Black Business</div>
            <div className="text-xs text-gray-400">Community • Commerce • Impact</div>
          </div>
          <button className="ml-auto lg:hidden text-gray-400" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <nav className="px-3 py-3 space-y-1 overflow-y-auto h-[calc(100vh-160px)]">
          {NAV.map(({ to, label, icon: Icon, end, highlight, badge, badgeTint, unread: showUnread }) => (
            <NavLink key={to} to={to} end={end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${to.replace(/\W+/g, '-') || 'home'}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'bg-green-600/15 text-green-400 border border-green-600/30'
                  : highlight
                    ? (badgeTint === 'emerald'
                        ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/15'
                        : 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/15')
                    : 'text-gray-300 hover:bg-white/5'
              }`}>
              <Icon size={18} />{label}
              {showUnread && unread > 0 && (
                <span data-testid="nav-messages-unread" className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5">{unread}</span>
              )}
              {highlight && !window.location.pathname.startsWith(to) && (
                <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${badgeTint === 'emerald' ? 'bg-emerald-400 text-black' : 'bg-yellow-400 text-black'}`}>{badge || 'Buy'}</span>
              )}
            </NavLink>
          ))}
          {user?.isBusinessOwner && (
            <>
              <div className="mt-4 mb-1 px-3 text-[11px] uppercase tracking-wider text-gray-500 font-semibold flex items-center justify-between">
                <span>For Business Owners</span>
                {tier?.label && <span className="text-[10px] text-yellow-300 normal-case font-semibold">{tier.label}</span>}
              </div>
              {BUSINESS_NAV.map(({ to, label, icon: Icon, feature }) => {
                const locked = feature && !hasFeature(feature);
                return (
                  <NavLink key={to} to={locked ? '/business/upgrade' : to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-${to.replace(/\W+/g,'-')}${locked ? '-locked' : ''}`}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive && !locked ? 'bg-green-600/15 text-green-400 border border-green-600/30' : locked ? 'text-gray-500 hover:bg-yellow-500/10' : 'text-gray-300 hover:bg-white/5'}`}>
                    <Icon size={18} />{label}
                    {locked && <Lock size={12} className="ml-auto text-yellow-400" />}
                  </NavLink>
                );
              })}
            </>
          )}
          {user?.isAdmin && (
            <>
              <div className="mt-4 mb-1 px-3 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Admin</div>
              <NavLink to="/admin" onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? 'bg-green-600/15 text-green-400 border border-green-600/30' : 'text-gray-300 hover:bg-white/5'}`}>
                <ShieldAlert size={18} /> Admin Panel
              </NavLink>
            </>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 border-t border-[#1f1f1f] p-3">
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:bg-white/5">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 bg-black/90 backdrop-blur border-b border-[#1f1f1f]">
          <div className="flex items-center gap-3 px-4 lg:px-8 h-16">
            <button className="lg:hidden text-gray-300" onClick={() => setOpen(true)}><Menu size={22} /></button>
            <div className="font-semibold hidden sm:block">{titleFor(loc.pathname)}</div>
            <div className="ml-auto flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-600/15 border border-green-600/30 text-green-400 text-sm">
                <Award size={14} /> {user?.points?.toLocaleString() || 0} pts
              </div>
              <NotificationsBell />
              <NavLink to="/profile" className="w-9 h-9 rounded-full overflow-hidden border border-[#2a2a2a]">
                <img src={filesApi.urlFor(user?.avatar)} alt="me" className="w-full h-full object-cover" />
              </NavLink>
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-6 pb-24 lg:pb-8">
          <Outlet />
        </main>
        <WelcomeModal />
        {/* Bottom nav mobile */}
        <nav className="fixed bottom-0 left-0 right-0 lg:hidden bg-[#0a0a0a] border-t border-[#1f1f1f] z-30">
          <div className="grid grid-cols-5">
            {BOTTOM.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end}
                className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[11px] ${isActive ? 'text-green-400' : 'text-gray-400'}`}>
                <Icon size={20} />{label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
}

function titleFor(path) {
  const map = { '/':'Home', '/marketing':'Public Site', '/marketing/landing':'Landing Page Builder', '/marketing/contacts':'Contact List', '/marketing/surveys':'Surveys', '/marketing/campaigns':'Campaigns', '/marketing/auto-responder':'Auto-Responder', '/admin/auto-responder':'Auto-Responder (Admin)', '/profile/interests':'My Interests', '/directory':'Directory', '/auction':'Auction Store', '/dealbin':'Deal Bin', '/donate':'Donate an Item', '/store':'Online Store', '/loyalty':'Loyalty Points', '/community':'Community', '/messages':'Messages', '/newsletter':'Newsletter', '/coupons':'Coupons', '/dating':'Dating', '/events':'Events', '/my/tickets':'My Tickets', '/news':'News Feed', '/leaderboard':'Leaderboard', '/funds':'Community Funds', '/business-dashboard':'Business Dashboard', '/business/manage':'Manage Listings', '/business/coupons':'My Coupons', '/business/loyalty':'Loyalty Program', '/business/reviews':'Review Verification', '/business/auctions':'My Auction Items', '/business/upgrade':'Upgrade Plan', '/profile':'Profile' };
  return map[path] || 'PBB';
}
